import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Building,
  MapPin,
  Users,
  Briefcase,
  Phone,
  Mail,
  Globe,
  Save,
  X,
  Edit3,
  CheckCircle2,
  Clock,
  XCircle,
  Calendar,
  Shield,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import { toast } from "sonner";
import { institutionsAPI } from "../../services/api";

interface Institution {
  id: string;
  name: string;
  type: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  address_line1: string | null;
  address_line2: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  total_students: number | null;
  staff_count: number | null;
  departments: string[] | null;
  trustee_name: string | null;
  trustee_contact: string | null;
  status: string | null;
  created_at: string | null;
}

export default function Institutions() {
  const [institution, setInstitution] = useState<Institution | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Institution>>({});
  const { user } = useAuth();
  const navigate = usePrefixedNavigate();

  const fetchInstitution = useCallback(async () => {
    if (!user) {
      return;
    }
    try {
      const data = await institutionsAPI.getAll();
      const list = Array.isArray(data) ? data : ((data as any)?.results ?? []);
      setInstitution(list[0] ?? null);
    } catch (error: any) {
      console.error("Error fetching institution:", error);
      const isNotFound =
        error?.response?.status === 404 ||
        error?.response?.data?.message?.toLowerCase().includes("not found");
      if (!isNotFound) {
        toast.error("Failed to load institution details");
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchInstitution();
    }
  }, [user, fetchInstitution]);

  useEffect(() => {
    if (institution) {
      setFormData(institution);
    }
  }, [institution]);

  const handleSave = async () => {
    if (!institution?.id) {
      return;
    }

    setSaving(true);
    try {
      await institutionsAPI.update(institution.id, {
        name: formData.name,
        type: formData.type ?? null,
        email: formData.email ?? null,
        phone: formData.phone ?? null,
        website: formData.website ?? null,
        address_line1: formData.address_line1 ?? null,
        address_line2: formData.address_line2 ?? null,
        city: formData.city ?? null,
        state: formData.state ?? null,
        pincode: formData.pincode ?? null,
        total_students: formData.total_students ?? null,
        staff_count: formData.staff_count ?? null,
        trustee_name: formData.trustee_name ?? null,
        trustee_contact: formData.trustee_contact ?? null,
      });

      toast.success("Institution details updated successfully");
      setIsEditing(false);
      fetchInstitution();
    } catch (error) {
      console.error("Error updating institution:", error);
      toast.error("Failed to update institution details");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(institution ?? {});
    setIsEditing(false);
  };

  const updateField = (
    field: keyof Institution,
    value: string | number | null
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getStatusConfig = (status: string | null) => {
    switch (status) {
      case "approved":
        return {
          icon: CheckCircle2,
          label: "Approved",
          className: "bg-emerald-50 text-emerald-700 border-emerald-200",
          iconClass: "text-emerald-600",
        };
      case "pending":
        return {
          icon: Clock,
          label: "Pending Review",
          className: "bg-amber-50 text-amber-700 border-amber-200",
          iconClass: "text-amber-600",
        };
      case "rejected":
        return {
          icon: XCircle,
          label: "Rejected",
          className: "bg-red-50 text-red-700 border-red-200",
          iconClass: "text-red-600",
        };
      default:
        return {
          icon: Clock,
          label: "Unknown",
          className: "bg-gray-50 text-gray-700 border-gray-200",
          iconClass: "text-gray-600",
        };
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="space-y-4 text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
          <p className="text-muted-foreground">
            Loading institution details...
          </p>
        </div>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Institution</h1>
          <p className="text-muted-foreground">
            View and manage your institution details
          </p>
        </div>
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10">
              <Building className="h-10 w-10 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-semibold">
              No Institution Registered
            </h3>
            <p className="mx-auto mb-6 max-w-sm text-muted-foreground">
              You haven&apos;t registered an institution yet. Complete the
              onboarding process to get started.
            </p>
            <Button
              onClick={() => navigate("/institution/onboarding")}
              size="lg"
            >
              Register Your Institution
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = getStatusConfig(institution.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Institution</h1>
          <p className="text-muted-foreground">
            View and manage your institution details
          </p>
        </div>
        <div className="flex gap-3">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)} variant="outline">
              <Edit3 className="mr-2 h-4 w-4" />
              Edit Details
            </Button>
          )}
        </div>
      </div>

      {/* Status Alert for Pending */}
      {institution.status === "pending" && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-900">
                  Application Under Review
                </h4>
                <p className="mt-1 text-sm text-amber-700">
                  Your institution registration is being reviewed by our team.
                  You&apos;ll be notified once it&apos;s approved.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Institution Header Card */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-2xl border-2 border-primary/20 bg-primary/10">
              <Building className="h-10 w-10 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <h2 className="truncate text-2xl font-bold text-foreground">
                  {institution.name}
                </h2>
                <Badge className={`${statusConfig.className} gap-1.5 border`}>
                  <StatusIcon
                    className={`h-3.5 w-3.5 ${statusConfig.iconClass}`}
                  />
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="capitalize text-muted-foreground">
                {institution.type ?? "Educational Institution"}
              </p>
            </div>
          </div>
        </div>

        <CardContent className="p-6">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Location
                </p>
                <p className="truncate font-semibold">
                  {institution.city ?? "N/A"}, {institution.state ?? "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Students
                </p>
                <p className="font-semibold">
                  {institution.total_students?.toLocaleString() ?? 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Briefcase className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Staff
                </p>
                <p className="font-semibold">
                  {institution.staff_count?.toLocaleString() ?? 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Registered
                </p>
                <p className="font-semibold">
                  {institution.created_at
                    ? new Date(institution.created_at).toLocaleDateString(
                        "en-IN",
                        { month: "short", year: "numeric" }
                      )
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabbed Form Layout */}
      <Card>
        <CardHeader className="border-b bg-muted/30">
          <CardTitle className="text-lg">Institution Details</CardTitle>
          <CardDescription>
            Complete information about your institution
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="mb-8 grid h-auto w-full grid-cols-4 p-1">
              <TabsTrigger value="basic" className="py-2.5 text-sm">
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="address" className="py-2.5 text-sm">
                Address
              </TabsTrigger>
              <TabsTrigger value="capacity" className="py-2.5 text-sm">
                Capacity
              </TabsTrigger>
              <TabsTrigger value="management" className="py-2.5 text-sm">
                Management
              </TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="mt-0 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    Institution Name
                  </Label>
                  {isEditing ? (
                    <Input
                      id="name"
                      value={formData.name ?? ""}
                      onChange={e => updateField("name", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">{institution.name}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-sm font-medium">
                    Institution Type
                  </Label>
                  {isEditing ? (
                    <Input
                      id="type"
                      value={formData.type ?? ""}
                      onChange={e => updateField("type", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium capitalize">
                        {institution.type ?? "Not specified"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  {isEditing ? (
                    <Input
                      id="email"
                      type="email"
                      value={formData.email ?? ""}
                      onChange={e => updateField("email", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.email ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone Number
                  </Label>
                  {isEditing ? (
                    <Input
                      id="phone"
                      value={formData.phone ?? ""}
                      onChange={e => updateField("phone", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.phone ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="website" className="text-sm font-medium">
                    Website
                  </Label>
                  {isEditing ? (
                    <Input
                      id="website"
                      value={formData.website ?? ""}
                      onChange={e => updateField("website", e.target.value)}
                      placeholder="https://example.com"
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      {institution.website ? (
                        <a
                          href={institution.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate font-medium text-primary hover:underline"
                        >
                          {institution.website}
                        </a>
                      ) : (
                        <span className="font-medium text-muted-foreground">
                          Not provided
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Address Tab */}
            <TabsContent value="address" className="mt-0 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label
                    htmlFor="address_line1"
                    className="text-sm font-medium"
                  >
                    Address Line 1
                  </Label>
                  {isEditing ? (
                    <Input
                      id="address_line1"
                      value={formData.address_line1 ?? ""}
                      onChange={e =>
                        updateField("address_line1", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.address_line1 ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label
                    htmlFor="address_line2"
                    className="text-sm font-medium"
                  >
                    Address Line 2
                  </Label>
                  {isEditing ? (
                    <Input
                      id="address_line2"
                      value={formData.address_line2 ?? ""}
                      onChange={e =>
                        updateField("address_line2", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.address_line2 ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-sm font-medium">
                    City
                  </Label>
                  {isEditing ? (
                    <Input
                      id="city"
                      value={formData.city ?? ""}
                      onChange={e => updateField("city", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.city ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state" className="text-sm font-medium">
                    State
                  </Label>
                  {isEditing ? (
                    <Input
                      id="state"
                      value={formData.state ?? ""}
                      onChange={e => updateField("state", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.state ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode" className="text-sm font-medium">
                    Pincode
                  </Label>
                  {isEditing ? (
                    <Input
                      id="pincode"
                      value={formData.pincode ?? ""}
                      onChange={e => updateField("pincode", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.pincode ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Capacity Tab */}
            <TabsContent value="capacity" className="mt-0 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label
                    htmlFor="total_students"
                    className="text-sm font-medium"
                  >
                    Total Students
                  </Label>
                  {isEditing ? (
                    <Input
                      id="total_students"
                      type="number"
                      value={formData.total_students ?? ""}
                      onChange={e =>
                        updateField(
                          "total_students",
                          parseInt(e.target.value) || null
                        )
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.total_students?.toLocaleString() ?? 0}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="staff_count" className="text-sm font-medium">
                    Staff Count
                  </Label>
                  {isEditing ? (
                    <Input
                      id="staff_count"
                      type="number"
                      value={formData.staff_count ?? ""}
                      onChange={e =>
                        updateField(
                          "staff_count",
                          parseInt(e.target.value) || null
                        )
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.staff_count?.toLocaleString() ?? 0}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">Departments</Label>
                  <div className="flex min-h-[60px] flex-wrap gap-2 rounded-lg border bg-muted/50 p-4">
                    {institution.departments &&
                    institution.departments.length > 0 ? (
                      institution.departments.map(dept => (
                        <Badge
                          key={dept}
                          variant="secondary"
                          className="border-0 bg-primary/10 px-3 py-1.5 text-primary"
                        >
                          {dept}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No departments added
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Management Tab */}
            <TabsContent value="management" className="mt-0 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="trustee_name" className="text-sm font-medium">
                    Trustee Name
                  </Label>
                  {isEditing ? (
                    <Input
                      id="trustee_name"
                      value={formData.trustee_name ?? ""}
                      onChange={e =>
                        updateField("trustee_name", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.trustee_name ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="trustee_contact"
                    className="text-sm font-medium"
                  >
                    Trustee Contact
                  </Label>
                  {isEditing ? (
                    <Input
                      id="trustee_contact"
                      value={formData.trustee_contact ?? ""}
                      onChange={e =>
                        updateField("trustee_contact", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.trustee_contact ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Status</Label>
                  <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                    <Badge
                      className={`${statusConfig.className} gap-1.5 border`}
                    >
                      <StatusIcon
                        className={`h-3.5 w-3.5 ${statusConfig.iconClass}`}
                      />
                      {statusConfig.label}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Registration Date
                  </Label>
                  <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {institution.created_at
                        ? new Date(institution.created_at).toLocaleDateString(
                            "en-IN",
                            { day: "numeric", month: "long", year: "numeric" }
                          )
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
